import { Empty, Tag, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faDrawPolygon, faFileCode, faLocationDot, faRoute, faShapes, faTrash } from "@fortawesome/free-solid-svg-icons";

//STYLES
import { CardResumo, ContainerConversor, ListaFeicoes } from "./styles";

//APP
import { errorAlert, successAlert } from "../../../utils/alert";
import { converterParaShapefile, listarFeicoes, lerArquivoKml, obterNomeBase, opcoesFormato, resumoGeometrias } from "./meta";

//COMPONENTS
import ButtonCustom from "../../../Components/ButtonCustom";
import Carregando from "../../../Components/CustomUi/loading";
import { Render } from "../../../Components/CustomUi/render";
import { Inline } from "../../../Components/CustomUi/inline";
import { Select } from "../../../Components/CustomUi/Select";
import { DefautContainer } from "../../../Components/CustomUi/defaultContainer";
import { UploadFileCustom } from "../../../Components/CustomUi/uploadFileCustom";
import { PopSelect } from "../../../Components/CustomUi/PopSelect";

/**limite de feições exibidas na pré-visualização (a conversão sempre usa todas) */
const LIMITE_PREVIA = 300;

//#region APP
export const Conversor: React.FC = () => {

    //#region STATES
    //ARQUIVO
    const [arquivo, setArquivo] = useState<File>(null);
    const [geojson, setGeojson] = useState<any>(null);
    const [carregando_arquivo, setCarregando_arquivo] = useState<boolean>(false);
    const [upload_key, setUpload_key] = useState<number>(0);
    //FORMATO DE SAÍDA
    const [nome_formato_selecionado, setNome_formato_selecionado] = useState<string>(opcoesFormato[0].nome);
    const [cod_formato_selecionado, setCod_formato_selecionado] = useState<number>(opcoesFormato[0].id);
    const [open_select_formato, setOpen_select_formato] = useState<boolean>(false);
    //CONVERSÃO
    const [convertendo, setConvertendo] = useState<boolean>(false);
    //#endregion

    //CONST
    const formato_selecionado = opcoesFormato.find(item => item.id === cod_formato_selecionado);
    const resumo = useMemo(() => (geojson ? resumoGeometrias(geojson) : null), [geojson]);
    const feicoes = useMemo(() => (geojson ? listarFeicoes(geojson) : []), [geojson]);

    //#region FUNCTIONS
    /**lê e valida o KML assim que o arquivo é selecionado */
    async function onFileSelect(files: File[]) {
        const file = files?.[0];

        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.kml')) {
            errorAlert('Formato inválido! Selecione um arquivo .kml');
            limpar();
            return;
        }

        setCarregando_arquivo(true);
        try {
            const novo_geojson = await lerArquivoKml(file);
            setArquivo(file);
            setGeojson(novo_geojson);
        }
        catch (error) {
            errorAlert(error?.message ?? 'Falha ao ler o arquivo KML!');
            limpar();
        }
        finally {
            setCarregando_arquivo(false);
        }
    }

    /**converte o KML importado e baixa no formato selecionado */
    async function converter() {
        if (!geojson || !formato_selecionado) return;

        setConvertendo(true);
        try {
            await converterParaShapefile(geojson, obterNomeBase(arquivo?.name), formato_selecionado.formato);
            successAlert('Conversão concluída! O download foi iniciado.');
        }
        catch (error) {
            console.error('Erro ao converter para Shapefile:', error);
            errorAlert('Ocorreu um erro durante a conversão!');
        }
        finally {
            setConvertendo(false);
        }
    }

    /**limpa o arquivo importado */
    function limpar() {
        setArquivo(null);
        setGeojson(null);
        /**alterar a key remonta o componente de upload, limpando o arquivo exibido nele */
        setUpload_key(atual => atual + 1);
    }
    //#endregion

    /**legenda do tooltip do botão converter */
    function tooltipConverter() {
        if (!geojson) return 'Importe um arquivo KML primeiramente';
        if (!formato_selecionado) return 'Selecione o formato de saída';
        return undefined;
    }

    /**ícone correspondente ao tipo da geometria */
    function iconeTipo(tipo: string) {
        if (tipo === 'Polígono') return faDrawPolygon;
        if (tipo === 'Linha') return faRoute;
        return faLocationDot;
    }

    //#region APP
    return (
        <DefautContainer
            title="Conversor de KML"
        >
            <ContainerConversor>
                <Inline alignItems="flex-end" gap={15}>
                    <div style={{ width: 300, minWidth: 200 }}>
                        <UploadFileCustom
                            key={upload_key}
                            label="Importar arquivo KML"
                            accept=".kml"
                            onFileSelect={onFileSelect}
                            style={{ height: 65 }}
                        />
                    </div>

                    <PopSelect
                        label="Formato de saída"
                        empty_placeholder="Selecione o formato"
                        icon={faShapes}
                        value={nome_formato_selecionado}
                        onChange={setNome_formato_selecionado}
                        items={opcoesFormato}
                        descricaoItem="nome"
                        idItem="id"
                    />

                    <Tooltip title={tooltipConverter()}><span>
                        <ButtonCustom
                            onClick={converter}
                            icon={<FontAwesomeIcon icon={faDownload} />}
                            loading={convertendo}
                            disabled={!geojson || !formato_selecionado || carregando_arquivo}
                        >
                            Converter
                        </ButtonCustom>
                    </span></Tooltip>

                    <Tooltip title="Remover arquivo importado"><span>
                        <ButtonCustom
                            onClick={limpar}
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            type="tertiary"
                            danger
                            hidden={!arquivo}
                            disabled={convertendo}
                        />
                    </span></Tooltip>
                </Inline>

                <Render condition={carregando_arquivo}>
                    <Carregando
                        animation
                        legenda="Lendo arquivo KML..."
                        justifyContent="center"
                        height={300}
                    />
                </Render>

                <Render condition={!!geojson && !carregando_arquivo}>
                    <CardResumo>
                        <div className="CardResumo__header">
                            <div className="CardResumo__nome_arquivo">
                                <FontAwesomeIcon icon={faFileCode} />
                                <span>{arquivo?.name}</span>
                            </div>

                            <div className="CardResumo__tags">
                                <Tag color="green">{resumo?.total} {resumo?.total === 1 ? 'feição' : 'feições'}</Tag>
                                {resumo?.poligonos > 0 && <Tag>{resumo?.poligonos} {resumo?.poligonos === 1 ? 'polígono' : 'polígonos'}</Tag>}
                                {resumo?.linhas > 0 && <Tag>{resumo?.linhas} {resumo?.linhas === 1 ? 'linha' : 'linhas'}</Tag>}
                                {resumo?.pontos > 0 && <Tag>{resumo?.pontos} {resumo?.pontos === 1 ? 'ponto' : 'pontos'}</Tag>}
                            </div>
                        </div>

                        <Render condition={!!formato_selecionado}>
                            <div className="CardResumo__formato">
                                Saída: <b>{formato_selecionado?.nome}</b> — {formato_selecionado?.descricao}
                            </div>
                        </Render>
                    </CardResumo>

                    <ListaFeicoes>
                        <div className="ListaFeicoes__header">
                            <span>Nº</span>
                            <span>Nome</span>
                            <span>Tipo</span>
                        </div>

                        <div className="ListaFeicoes__body">
                            {feicoes.slice(0, LIMITE_PREVIA).map((feicao, index) => (
                                <div className="ListaFeicoes__row" key={index}>
                                    <span>{index + 1}</span>
                                    <span className="ListaFeicoes__row__nome">{feicao.nome}</span>
                                    <span className="ListaFeicoes__row__tipo">
                                        <FontAwesomeIcon icon={iconeTipo(feicao.tipo)} />
                                        {feicao.tipo}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <Render condition={feicoes.length > LIMITE_PREVIA}>
                            <div className="ListaFeicoes__rodape">
                                + {feicoes.length - LIMITE_PREVIA} feições não exibidas na pré-visualização (todas serão convertidas)
                            </div>
                        </Render>
                    </ListaFeicoes>
                </Render>

                <Render condition={!geojson && !carregando_arquivo}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ marginTop: 100 }}
                        description={<>Importe um arquivo <b>KML</b> para iniciar a conversão!</>}
                    />
                </Render>
            </ContainerConversor>
        </DefautContainer>
    )
}
//#endregion
